import type { Theme } from '@gonfalon/theme/types';

/**
 * react-select inline-style overrides that intentionally DUPLICATE Launchpad's
 * own listbox/popover styling, so these legacy selects stop looking out of place
 * next to native Launchpad components (`@launchpad-ui/components`).
 *
 * This is a deliberate copy, not a reuse. Launchpad exports class-name helpers
 * (`listBoxItemStyles`, `popoverStyles`, `headerStyles`, ...) rather than style
 * objects, and their interactive states are keyed off react-aria `data-*`
 * attributes that react-select does not emit. react-select also applies its own
 * emotion inline styles (which win over classes) unless the whole component runs
 * `unstyled`. Bridging all of that into this shared wrapper isn't worth the risk,
 * so instead we mirror Launchpad's look using the same design tokens its CSS
 * consumes (`--lp-color-*`, `--lp-border-radius-*`, `--lp-size-*`).
 *
 * Source of truth to keep in sync with: `@launchpad-ui/components` ListBox /
 * Popover (see its `style.css`) and `launchpad-experimental/ListBox`.
 */

const textColorPrimary = (themeValue: Theme) =>
  isDarkTheme(themeValue) ? 'var(--lp-color-text-ui-primary-base)' : '#212529'; // --lp-color-gray-950
const textColorTertiary = (themeValue: Theme) =>
  isDarkTheme(themeValue) ? 'var(--lp-color-text-ui-secondary)' : 'var(--lp-color-gray-600)'; // --lp-color-gray-600

export const selectCustomControlStyles = (_themeValue: Theme, isFocused: boolean, isDisabled: boolean) => ({
  // Match Launchpad's ComboBox/Select trigger. The Values control behaves like an
  // editable field, so we mirror Launchpad's *field* tokens (not its button
  // tokens): a field background, a 1px field border, and a medium border radius.
  backgroundColor: isDisabled ? 'var(--lp-color-bg-field-disabled)' : 'var(--lp-color-bg-field-base)',
  minHeight: '2rem',
  display: 'flex',
  overflow: 'visible',
  width: '100%',
  borderRadius: 'var(--lp-border-radius-medium)',
  borderColor: isDisabled ? 'var(--lp-color-border-field-disabled)' : 'var(--lp-color-border-field-base)',
  // Match Launchpad's focus treatment: a 2px inset focus ring drawn just inside
  // the control (react-aria's `[data-focus-within]`/`[data-focus-visible]` style),
  // instead of react-select's default blue glow + border-color swap. The
  // `outline-offset: -2px` keeps the ring aligned with the rounded border.
  outline: isFocused ? '2px solid var(--lp-color-shadow-interactive-focus)' : 0,
  outlineOffset: '-2px',
  boxShadow: 'none',
  '&:hover': {
    borderColor: isDisabled ? 'var(--lp-color-border-field-disabled)' : 'var(--lp-color-border-field-base)',
  },
});

export const selectCustomSingleValueStyles = {
  fontSize: 'var(--lp-font-size-200)',
  color: 'var(--lp-color-text-ui-primary-base)',
};

export const selectCustomMenuStyles = () => ({
  zIndex: 101, // One less than all modals and manager controls, and sticky menu
  // Match Launchpad's Popover: no border, soft layered shadow, and rounded corners.
  border: 'none',
  borderRadius: 'var(--lp-border-radius-medium)',
  backgroundColor: 'var(--lp-color-bg-overlay-secondary)',
  boxShadow:
    '0px 0px 1px 0px var(--lp-color-shadow-ui-secondary), 0px 0px 4px 0px var(--lp-color-shadow-ui-secondary), 0px 4px 8px 0px var(--lp-color-shadow-ui-secondary), 0px 2px 12px 0px var(--lp-color-shadow-ui-secondary)',
  fontSize: 'var(--lp-font-size-200)',
});

// Match Launchpad's ListBox: a small amount of padding around the options so the
// rounded hover/selected highlights don't touch the popover edges.
export const selectCustomMenuListStyles = {
  padding: 'var(--lp-size-4)',
  borderRadius: 'inherit',
};

// Match Launchpad's ComboBox/Select trigger inner padding so the value/placeholder
// sits one spacing unit (`--lp-spacing-300`) off the left edge instead of being
// flush against the border. `gap` spaces multi-select tag chips, and the
// max-height keeps a long list of tags scrollable instead of growing unbounded.
export const selectCustomValueContainerStyles = {
  padding: '2px var(--lp-spacing-300)',
  gap: 'var(--lp-spacing-200)',
  maxHeight: '10.625rem',
  overflow: 'auto',
};

export const selectCustomMenuPortalStyles = {
  zIndex: 101, // One less than all modals and manager controls, and sticky menu
};
export const selectCustomMultiValueStyles = {
  backgroundColor: 'var(--lp-color-bg-interactive-secondary-hover)',
  borderRadius: '3px',
  maxWidth: '32.5rem',
  // Chip spacing comes from the value container's `gap`, not per-chip margin.
  // Dropping react-select's default 2px margin keeps the trigger at Launchpad's
  // 2rem field height once value chips are present (otherwise the extra vertical
  // margin grows the control past the height of sibling Launchpad fields).
  margin: 0,
};

export const selectCustomInputStyles = {
  fontSize: 'var(--lp-font-size-200)',
  color: 'var(--lp-color-text-ui-primary-base)',
  // Drop react-select's default input margin/padding. The value container already
  // supplies the trigger's inner padding, and the default input box is taller than
  // a value chip — left in, it grows the control past Launchpad's 2rem field height.
  margin: 0,
  padding: 0,
};

export const selectCustomMultiValueLabelStyles = {
  color: 'var(--lp-color-text-ui-primary-base)',
  // Match Launchpad's field height: drop react-select's default vertical label
  // padding so a chip's height tracks its text, not text + padding.
  paddingBlock: 0,
};

export const selectCustomMultiValueRemoveStyles = {
  '&:hover': {
    backgroundColor: 'var(--lp-color-gray-400)',
    borderRadius: '4px',
    cursor: 'pointer',
  },
};

export const selectCustomPlaceholderStyles = (_themeValue: Theme) => ({
  fontSize: 'var(--lp-font-size-200)',
  // Match Launchpad's field placeholder token (lighter than body text), rather
  // than the darker tertiary text color used previously.
  color: 'var(--lp-color-text-field-placeholder)',
});

export const selectCustomIndicatorStyles = (themeValue: Theme) => ({
  color: textColorTertiary(themeValue),
  '&:hover': {
    color: textColorPrimary(themeValue),
    cursor: 'pointer',
  },
  // Match Launchpad's trigger: the caret sits one spacing unit (`--lp-spacing-300`)
  // off the right edge instead of being tucked into the corner.
  padding: '0 var(--lp-spacing-300)',
});

export const selectCustomOptionStyles = (
  _themeValue: Theme,
  isSelected: boolean,
  isFocused: boolean,
  isDisabled: boolean,
) => ({
  textOverflow: 'ellipsis',
  overflow: 'hidden',

  // state: default — match Launchpad's ListBoxItem: no border, rounded corners,
  // and a transparent background. The previous 2px solid border is what created
  // the heavy outline around focused/selected options.
  border: 'none',
  borderRadius: 'var(--lp-border-radius-regular)',
  backgroundColor: 'transparent',
  color: 'var(--lp-color-text-ui-primary-base)',
  padding: 'var(--lp-size-4) var(--lp-size-8)',
  cursor: isDisabled ? 'not-allowed' : 'pointer',

  // state: selected — subtle Launchpad "selected" fill (theme-aware token).
  ...(isSelected && {
    backgroundColor: 'var(--lp-color-bg-interactive-selected)',
    color: 'var(--lp-color-text-interactive-selected)',
  }),

  // state: focused (keyboard/mouse highlight) — Launchpad's hover/focus fill.
  // Applied after "selected" so navigating shows the highlight on top.
  ...(isFocused &&
    !isDisabled && {
      backgroundColor: 'var(--lp-color-bg-interactive-secondary-hover)',
    }),

  // state: disabled
  ...(isDisabled && {
    backgroundColor: 'transparent',
    color: 'var(--lp-color-text-interactive-disabled)',
  }),

  // state: hover (fallback for non-focused hover)
  '&:hover': {
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    backgroundColor: isDisabled ? undefined : 'var(--lp-color-bg-interactive-secondary-hover)',
  },

  // state: active/pressed
  '&:active': {
    backgroundColor: isDisabled ? undefined : 'var(--lp-color-bg-interactive-secondary-active)',
  },
});

const isDarkTheme = (themeValue: Theme) => themeValue === 'dark';
